import React, { useState, useEffect, useCallback } from 'react';
import AdminTableSkeleton from "../../components/layout/AdminTableSkeleton";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Plus, Save, X, RotateCcw, Edit, Trash2, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { PromptTab } from './aiFeatureTabs';

const EMPTY_CATEGORY = { name: '', weight: '', description: '', rubric: '', sortOrder: '' };
const EMPTY_JD = { roleName: '', roleSummary: '', responsibilities: '', requiredSkills: '', preferredMajors: '', typicalIndustries: '', sortOrder: '' };

export default function ResumeScoringManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('categories');

    // Categories state
    const [categories, setCategories] = useState([]);
    const [statusTab, setStatusTab] = useState('active');
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [newItem, setNewItem] = useState(EMPTY_CATEGORY);
    const [showNewForm, setShowNewForm] = useState(false);
    const [saving, setSaving] = useState(false);

    // Job Descriptions state
    const [jobDescriptions, setJobDescriptions] = useState([]);
    const [jdStatusTab, setJdStatusTab] = useState('active');
    const [editingJd, setEditingJd] = useState(null);
    const [newJd, setNewJd] = useState(EMPTY_JD);
    const [showNewJdForm, setShowNewJdForm] = useState(false);

    // Prompt state
    const [prompts, setPrompts] = useState([]);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [newPrompt, setNewPrompt] = useState({ featureName: 'resume_analysis', promptText: '' });
    const [showNewPrompt, setShowNewPrompt] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewing, setPreviewing] = useState(false);


    useEffect(() => {
        if (!user || !user.isAdmin) navigate('/userdashboard');
    }, [user, navigate]);

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/resume-scoring/categories');
            setCategories(res.data.categories || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchPrompts = useCallback(async () => {
        try {
            const res = await api.get('/admin/prompt-config');
            setPrompts(res.data.configs || []);
        } catch (err) {
            console.error('Error fetching prompts:', err);
        }
    }, []);

    const fetchJobDescriptions = useCallback(async () => {
        try {
            const res = await api.get('/admin/resume-scoring/job-descriptions');
            setJobDescriptions(res.data.jobDescriptions || []);
        } catch (err) {
            console.error('Error fetching job descriptions:', err);
        }
    }, []);

    useEffect(() => { fetchCategories(); fetchPrompts(); fetchJobDescriptions(); }, [fetchCategories, fetchPrompts, fetchJobDescriptions]);

    // ── Category handlers ──
    const handleCreateCategory = async () => {
        if (!newItem.name.trim()) return;
        const weight = parseInt(newItem.weight, 10);
        if (isNaN(weight) || weight < 0) return alert('Weight must be a non-negative number');
        setSaving(true);
        try {
            await api.post('/admin/resume-scoring/categories', {
                name: newItem.name, weight, description: newItem.description,
                rubric: newItem.rubric, sortOrder: parseInt(newItem.sortOrder, 10) || 0,
            });
            setNewItem(EMPTY_CATEGORY);
            setShowNewForm(false);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create category');
        } finally { setSaving(false); }
    };

    const handleUpdateCategory = async (item) => {
        const weight = parseInt(item.Weight, 10);
        if (isNaN(weight) || weight < 0) return alert('Weight must be a non-negative number');
        setSaving(true);
        try {
            await api.put(`/admin/resume-scoring/categories/${item.CategoryID}`, {
                name: item.Name, weight, description: item.Description || '',
                rubric: item.Rubric || '', sortOrder: parseInt(item.SortOrder, 10) || 0,
            });
            setEditing(null);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update category');
        } finally { setSaving(false); }
    };

    const handleDeleteCategory = async (item) => {
        if (!window.confirm(`Deactivate "${item.Name}"?`)) return;
        setSaving(true);
        try {
            await api.delete(`/admin/resume-scoring/categories/${item.CategoryID}`);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete category');
        } finally { setSaving(false); }
    };

    const handleRestoreCategory = async (item) => {
        setSaving(true);
        try {
            await api.patch(`/admin/resume-scoring/categories/${item.CategoryID}/restore`);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to restore category');
        } finally { setSaving(false); }
    };

    // ── Prompt handlers ──
    const handleCreatePrompt = async () => {
        if (!newPrompt.promptText.trim()) return;
        setSaving(true);
        try {
            await api.post('/admin/prompt-config', {
                featureName: newPrompt.featureName, promptText: newPrompt.promptText,
            });
            setNewPrompt({ featureName: 'resume_analysis', promptText: '' });
            setShowNewPrompt(false);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create prompt');
        } finally { setSaving(false); }
    };

    const handleUpdatePrompt = async (item) => {
        setSaving(true);
        try {
            await api.put(`/admin/prompt-config/${item.PromptConfigID}`, {
                promptText: item.PromptText,
            });
            setEditingPrompt(null);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update prompt');
        } finally { setSaving(false); }
    };

    const handlePublishPrompt = async (id) => {
        if (!window.confirm('Publish this prompt? It will become the active prompt for resume analysis.')) return;
        setSaving(true);
        try {
            await api.post(`/admin/prompt-config/${id}/publish`);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to publish prompt');
        } finally { setSaving(false); }
    };

    const handlePreviewPrompt = async (id) => {
        setPreviewing(true);
        try {
            const res = await api.post(`/admin/prompt-config/${id}/test`);
            setPreviewResult(res.data);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to preview prompt');
        } finally { setPreviewing(false); }
    };

    // ── Job Description handlers ──
    const handleCreateJd = async () => {
        if (!newJd.roleName.trim() || !newJd.roleSummary.trim()) return alert('Role name and summary are required');
        setSaving(true);
        try {
            await api.post('/admin/resume-scoring/job-descriptions', {
                ...newJd, sortOrder: parseInt(newJd.sortOrder, 10) || 0,
            });
            setNewJd(EMPTY_JD);
            setShowNewJdForm(false);
            await fetchJobDescriptions();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create job description');
        } finally { setSaving(false); }
    };

    const handleUpdateJd = async (item) => {
        if (!item.RoleName?.trim() || !item.RoleSummary?.trim()) return alert('Role name and summary are required');
        setSaving(true);
        try {
            await api.put(`/admin/resume-scoring/job-descriptions/${item.JobDescriptionID}`, {
                roleName: item.RoleName, roleSummary: item.RoleSummary,
                responsibilities: item.Responsibilities || '', requiredSkills: item.RequiredSkills || '',
                preferredMajors: item.PreferredMajors || '', typicalIndustries: item.TypicalIndustries || '',
                sortOrder: parseInt(item.SortOrder, 10) || 0,
            });
            setEditingJd(null);
            await fetchJobDescriptions();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update job description');
        } finally { setSaving(false); }
    };

    const handleDeleteJd = async (item) => {
        if (!window.confirm(`Deactivate "${item.RoleName}"?`)) return;
        setSaving(true);
        try {
            await api.patch(`/admin/resume-scoring/job-descriptions/${item.JobDescriptionID}/deactivate`);
            await fetchJobDescriptions();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to deactivate job description');
        } finally { setSaving(false); }
    };

    const handleRestoreJd = async (item) => {
        setSaving(true);
        try {
            await api.patch(`/admin/resume-scoring/job-descriptions/${item.JobDescriptionID}/restore`);
            await fetchJobDescriptions();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to restore job description');
        } finally { setSaving(false); }
    };

    const activeCategories = categories.filter((c) => c.isActive !== false && c.isActive !== 0);
    const inactiveCategories = categories.filter((c) => c.isActive === false || c.isActive === 0);
    const currentList = statusTab === 'active' ? activeCategories : inactiveCategories;
    const totalWeight = activeCategories.reduce((sum, c) => sum + (c.Weight || 0), 0);

    const activeJds = jobDescriptions.filter((j) => j.isActive !== false && j.isActive !== 0);
    const inactiveJds = jobDescriptions.filter((j) => j.isActive === false || j.isActive === 0);
    const currentJdList = jdStatusTab === 'active' ? activeJds : inactiveJds;

    const cardClass = `rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const inputClass = `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`;
    const labelClass = `block text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const btnPrimary = 'px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors flex items-center gap-2';
    const btnSecondary = `px-4 py-2 border rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`;

    if (loading) {
        return <AdminTableSkeleton isDarkMode={isDarkMode} maxW="max-w-7xl" withSubtitle tabs={2} rows={6} cols={4} />;
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                <header className="mb-6">
                    <button onClick={() => navigate('/admin/dashboard')} className="text-primary-600 hover:text-primary-700 mb-4 text-sm font-medium">
                        &larr; Back to Dashboard
                    </button>
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Resume Analysis Settings</h1>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Configure scoring categories and the AI prompt</p>
                </header>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    {[
                        { key: 'categories', label: 'Categories & Rubrics' },
                        { key: 'prompt', label: 'AI Prompt' },
                        { key: 'jobDescriptions', label: 'Job Descriptions' },
                        { key: 'checklist', label: 'Resume Checklist' },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => {
                                setActiveTab(t.key);
                                setEditing(null);
                                setEditingJd(null);
                                setEditingPrompt(null);
                                setShowNewForm(false);
                                setShowNewJdForm(false);
                                setShowNewPrompt(false);
                                setExpandedId(null);
                            }}
                            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                                activeTab === t.key
                                    ? 'bg-primary-600 text-white'
                                    : isDarkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ════════ CATEGORIES TAB ════════ */}
                {activeTab === 'categories' && (
                    <div className={cardClass}>
                        <div className={`flex items-center justify-between px-5 pt-4 pb-3`}>
                            <div className="flex gap-2">
                                {[
                                    { key: 'active', label: 'Active', items: activeCategories },
                                    { key: 'inactive', label: 'Inactive', items: inactiveCategories },
                                ].map((st) => (
                                    <button
                                        key={st.key}
                                        onClick={() => { setStatusTab(st.key); setEditing(null); }}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                            statusTab === st.key
                                                ? (st.key === 'inactive' ? 'bg-slate-700 text-white' : 'bg-primary-600 text-white')
                                                : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {st.label} ({st.items.length})
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                                    totalWeight === 100 ? 'bg-primary-100 text-primary-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                    Total Weight: {totalWeight}%{totalWeight !== 100 ? ' (should be 100%)' : ''}
                                </span>
                                {statusTab === 'active' && (
                                    <button onClick={() => setShowNewForm(!showNewForm)} className={btnPrimary}>
                                        <Plus size={16} /> Add Category
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* New category form */}
                        {showNewForm && statusTab === 'active' && (
                            <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                                <div className="grid grid-cols-12 gap-3 mb-3">
                                    <div className="col-span-4">
                                        <label className={labelClass}>Name</label>
                                        <input type="text" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className={inputClass} placeholder="Category name" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className={labelClass}>Weight %</label>
                                        <input type="number" min="0" max="100" value={newItem.weight} onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })} className={inputClass} />
                                    </div>
                                    <div className="col-span-5">
                                        <label className={labelClass}>Description</label>
                                        <input type="text" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className={inputClass} placeholder="What does this category evaluate?" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className={labelClass}>Sort</label>
                                        <input type="number" min="0" value={newItem.sortOrder} onChange={(e) => setNewItem({ ...newItem, sortOrder: e.target.value })} className={inputClass} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className={labelClass}>Grading Rubric</label>
                                    <textarea value={newItem.rubric} onChange={(e) => setNewItem({ ...newItem, rubric: e.target.value })} rows={5} className={inputClass} placeholder="A (85-100): ...&#10;B (65-84): ...&#10;C (45-64): ...&#10;D (25-44): ...&#10;F (0-24): ..." />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleCreateCategory} disabled={saving} className={btnPrimary}><Save size={16} /> Save</button>
                                    <button onClick={() => { setShowNewForm(false); setNewItem(EMPTY_CATEGORY); }} className={btnSecondary}><X size={16} /> Cancel</button>
                                </div>
                            </div>
                        )}

                        {/* Category list */}
                        <div className="px-5 pb-5">
                            {currentList.length === 0 ? (
                                <p className={`text-center py-12 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {statusTab === 'inactive' ? 'No inactive categories.' : 'No categories found.'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {currentList.map((item) => (
                                        <div key={item.CategoryID} className={`border rounded-xl transition-all ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                            {editing?.CategoryID === item.CategoryID ? (
                                                <div className="p-4">
                                                    <div className="grid grid-cols-12 gap-3 mb-3">
                                                        <div className="col-span-4">
                                                            <label className={labelClass}>Name</label>
                                                            <input type="text" value={editing.Name} onChange={(e) => setEditing({ ...editing, Name: e.target.value })} className={inputClass} />
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className={labelClass}>Weight %</label>
                                                            <input type="number" min="0" max="100" value={editing.Weight} onChange={(e) => setEditing({ ...editing, Weight: e.target.value })} className={inputClass} />
                                                        </div>
                                                        <div className="col-span-5">
                                                            <label className={labelClass}>Description</label>
                                                            <input type="text" value={editing.Description || ''} onChange={(e) => setEditing({ ...editing, Description: e.target.value })} className={inputClass} />
                                                        </div>
                                                        <div className="col-span-1">
                                                            <label className={labelClass}>Sort</label>
                                                            <input type="number" min="0" value={editing.SortOrder} onChange={(e) => setEditing({ ...editing, SortOrder: e.target.value })} className={inputClass} />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className={labelClass}>Grading Rubric</label>
                                                        <textarea value={editing.Rubric || ''} onChange={(e) => setEditing({ ...editing, Rubric: e.target.value })} rows={6} className={inputClass} />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleUpdateCategory(editing)} disabled={saving} className={btnPrimary}><Save size={16} /> Save</button>
                                                        <button onClick={() => setEditing(null)} className={btnSecondary}><X size={16} /> Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3 flex-1">
                                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.Name}</span>
                                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-primary-100 text-primary-700'}`}>
                                                                {item.Weight}%
                                                            </span>
                                                            {item.Description && (
                                                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>— {item.Description}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {item.Rubric && (
                                                                <button
                                                                    onClick={() => setExpandedId(expandedId === item.CategoryID ? null : item.CategoryID)}
                                                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}
                                                                    title="View rubric"
                                                                >
                                                                    {expandedId === item.CategoryID ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                                </button>
                                                            )}
                                                            <button onClick={() => setEditing({ ...item })} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                                                <Edit size={16} />
                                                            </button>
                                                            {statusTab === 'active' ? (
                                                                <button onClick={() => handleDeleteCategory(item)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Deactivate">
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            ) : (
                                                                <button onClick={() => handleRestoreCategory(item)} className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Restore">
                                                                    <RotateCcw size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {expandedId === item.CategoryID && item.Rubric && (
                                                        <pre className={`mt-3 p-3 rounded-lg text-sm whitespace-pre-wrap font-sans ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                                                            {item.Rubric}
                                                        </pre>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ════════ PROMPT TAB ════════ */}
                {activeTab === 'prompt' && (
                    <div className={cardClass}>
                        <div className="flex items-center justify-between px-5 pt-4 pb-3">
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>AI Prompt Templates</h2>
                            <button onClick={() => setShowNewPrompt(!showNewPrompt)} className={btnPrimary}>
                                <Plus size={16} /> New Version
                            </button>
                        </div>

                        <p className={`px-5 pb-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Use <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{'{categories_block}'}</code> and <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{'{num_categories}'}</code> as placeholders. Categories, weights, and rubrics are injected automatically from the Categories tab.
                        </p>

                        {showNewPrompt && (
                            <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                                <div className="mb-3">
                                    <label className={labelClass}>Feature</label>
                                    <input type="text" value={newPrompt.featureName} onChange={(e) => setNewPrompt({ ...newPrompt, featureName: e.target.value })} className={inputClass} />
                                </div>
                                <div className="mb-3">
                                    <label className={labelClass}>Prompt Template</label>
                                    <textarea value={newPrompt.promptText} onChange={(e) => setNewPrompt({ ...newPrompt, promptText: e.target.value })} rows={12} className={`${inputClass} font-mono text-xs`} placeholder="Enter your prompt template..." />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleCreatePrompt} disabled={saving} className={btnPrimary}><Save size={16} /> Save Draft</button>
                                    <button onClick={() => { setShowNewPrompt(false); setNewPrompt({ featureName: 'resume_analysis', promptText: '' }); }} className={btnSecondary}><X size={16} /> Cancel</button>
                                </div>
                            </div>
                        )}

                        <div className="px-5 pb-5 space-y-3">
                            {prompts.length === 0 ? (
                                <div className={`text-center py-12 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    <p className="text-sm mb-2">No custom prompts yet. The system is using the built-in default prompt.</p>
                                    <p className="text-xs">Create a new version to customize the AI's behavior.</p>
                                </div>
                            ) : (
                                prompts.map((p) => (
                                    <div key={p.PromptConfigID} className={`border rounded-xl p-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        {editingPrompt?.PromptConfigID === p.PromptConfigID ? (
                                            <>
                                                <div className="mb-3">
                                                    <label className={labelClass}>Prompt Template</label>
                                                    <textarea value={editingPrompt.PromptText} onChange={(e) => setEditingPrompt({ ...editingPrompt, PromptText: e.target.value })} rows={14} className={`${inputClass} font-mono text-xs`} />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleUpdatePrompt(editingPrompt)} disabled={saving} className={btnPrimary}><Save size={16} /> Save</button>
                                                    <button onClick={() => setEditingPrompt(null)} className={btnSecondary}><X size={16} /> Cancel</button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{p.FeatureName}</span>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>v{p.Version}</span>
                                                        {p.IsPublished ? (
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-semibold">Published</span>
                                                        ) : (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>Draft</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => handlePreviewPrompt(p.PromptConfigID)} disabled={previewing} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`} title="Preview rendered prompt">
                                                            <Eye size={16} />
                                                        </button>
                                                        <button onClick={async () => {
                                                            try {
                                                                const res = await api.get(`/admin/prompt-config/${p.PromptConfigID}`);
                                                                setEditingPrompt(res.data.prompt || res.data);
                                                            } catch (err) {
                                                                alert(err.response?.data?.error || "Failed to load prompt");
                                                            }
                                                        }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                                            <Edit size={16} />
                                                        </button>
                                                        {!p.IsPublished && (
                                                            <button onClick={() => handlePublishPrompt(p.PromptConfigID)} className={btnPrimary + ' !px-3 !py-1.5 !text-xs'}>
                                                                Publish
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {p.PublishedOn && (
                                                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                        Published: {new Date(p.PublishedOn).toLocaleString()}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))
                            )}

                            {previewResult && (
                                <div className={`border rounded-xl p-4 mt-4 ${isDarkMode ? 'border-primary-800 bg-primary-900/20' : 'border-primary-200 bg-primary-50'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-primary-400' : 'text-primary-700'}`}>Rendered Preview</h3>
                                        <button onClick={() => setPreviewResult(null)} className={`p-1 rounded ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}><X size={16} /></button>
                                    </div>
                                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        {previewResult.categoryCount} categories &middot; {previewResult.charCount} characters
                                    </p>
                                    <pre className={`p-3 rounded-lg text-xs whitespace-pre-wrap font-mono max-h-96 overflow-y-auto ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-white text-slate-700 border border-slate-200'}`}>
                                        {previewResult.rendered}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ════════ JOB DESCRIPTIONS TAB ════════ */}
                {activeTab === 'jobDescriptions' && (
                    <div className={cardClass}>
                        <div className="flex items-center justify-between px-5 pt-4 pb-3">
                            <div className="flex gap-2">
                                {[
                                    { key: 'active', label: 'Active', items: activeJds },
                                    { key: 'inactive', label: 'Inactive', items: inactiveJds },
                                ].map((st) => (
                                    <button
                                        key={st.key}
                                        onClick={() => { setJdStatusTab(st.key); setEditingJd(null); }}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                            jdStatusTab === st.key
                                                ? (st.key === 'inactive' ? 'bg-slate-700 text-white' : 'bg-primary-600 text-white')
                                                : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {st.label} ({st.items.length})
                                    </button>
                                ))}
                            </div>
                            {jdStatusTab === 'active' && (
                                <button onClick={() => setShowNewJdForm(!showNewJdForm)} className={btnPrimary}>
                                    <Plus size={16} /> Add Role
                                </button>
                            )}
                        </div>

                        {showNewJdForm && jdStatusTab === 'active' && (
                            <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                                <div className="grid grid-cols-12 gap-3 mb-3">
                                    <div className="col-span-5">
                                        <label className={labelClass}>Role Name</label>
                                        <input type="text" value={newJd.roleName} onChange={(e) => setNewJd({ ...newJd, roleName: e.target.value })} className={inputClass} placeholder="e.g. Software Engineer I" />
                                    </div>
                                    <div className="col-span-6">
                                        <label className={labelClass}>Typical Industries</label>
                                        <input type="text" value={newJd.typicalIndustries} onChange={(e) => setNewJd({ ...newJd, typicalIndustries: e.target.value })} className={inputClass} placeholder="Technology; Finance; Healthcare" />
                                    </div>
                                    <div className="col-span-1">
                                        <label className={labelClass}>Sort</label>
                                        <input type="number" min="0" value={newJd.sortOrder} onChange={(e) => setNewJd({ ...newJd, sortOrder: e.target.value })} className={inputClass} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className={labelClass}>Role Summary</label>
                                    <textarea value={newJd.roleSummary} onChange={(e) => setNewJd({ ...newJd, roleSummary: e.target.value })} rows={3} className={inputClass} placeholder="Brief description of the role..." />
                                </div>
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className={labelClass}>Key Responsibilities (semicolon-separated)</label>
                                        <textarea value={newJd.responsibilities} onChange={(e) => setNewJd({ ...newJd, responsibilities: e.target.value })} rows={3} className={inputClass} placeholder="Develop features; Write tests; Code reviews" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Required Skills (semicolon-separated)</label>
                                        <textarea value={newJd.requiredSkills} onChange={(e) => setNewJd({ ...newJd, requiredSkills: e.target.value })} rows={3} className={inputClass} placeholder="Python; Git; Problem-solving" />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className={labelClass}>Preferred Majors (semicolon-separated)</label>
                                    <input type="text" value={newJd.preferredMajors} onChange={(e) => setNewJd({ ...newJd, preferredMajors: e.target.value })} className={inputClass} placeholder="Computer Science; Software Engineering" />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleCreateJd} disabled={saving} className={btnPrimary}><Save size={16} /> Save</button>
                                    <button onClick={() => { setShowNewJdForm(false); setNewJd(EMPTY_JD); }} className={btnSecondary}><X size={16} /> Cancel</button>
                                </div>
                            </div>
                        )}

                        <div className="px-5 pb-5">
                            {currentJdList.length === 0 ? (
                                <p className={`text-center py-12 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {jdStatusTab === 'inactive' ? 'No inactive job descriptions.' : 'No job descriptions found.'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {currentJdList.map((item) => (
                                        <div key={item.JobDescriptionID} className={`border rounded-xl transition-all ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                            {editingJd?.JobDescriptionID === item.JobDescriptionID ? (
                                                <div className="p-4">
                                                    <div className="grid grid-cols-12 gap-3 mb-3">
                                                        <div className="col-span-5">
                                                            <label className={labelClass}>Role Name</label>
                                                            <input type="text" value={editingJd.RoleName} onChange={(e) => setEditingJd({ ...editingJd, RoleName: e.target.value })} className={inputClass} />
                                                        </div>
                                                        <div className="col-span-6">
                                                            <label className={labelClass}>Typical Industries</label>
                                                            <input type="text" value={editingJd.TypicalIndustries || ''} onChange={(e) => setEditingJd({ ...editingJd, TypicalIndustries: e.target.value })} className={inputClass} />
                                                        </div>
                                                        <div className="col-span-1">
                                                            <label className={labelClass}>Sort</label>
                                                            <input type="number" min="0" value={editingJd.SortOrder} onChange={(e) => setEditingJd({ ...editingJd, SortOrder: e.target.value })} className={inputClass} />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className={labelClass}>Role Summary</label>
                                                        <textarea value={editingJd.RoleSummary} onChange={(e) => setEditingJd({ ...editingJd, RoleSummary: e.target.value })} rows={3} className={inputClass} />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                                        <div>
                                                            <label className={labelClass}>Key Responsibilities</label>
                                                            <textarea value={editingJd.Responsibilities || ''} onChange={(e) => setEditingJd({ ...editingJd, Responsibilities: e.target.value })} rows={3} className={inputClass} />
                                                        </div>
                                                        <div>
                                                            <label className={labelClass}>Required Skills</label>
                                                            <textarea value={editingJd.RequiredSkills || ''} onChange={(e) => setEditingJd({ ...editingJd, RequiredSkills: e.target.value })} rows={3} className={inputClass} />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className={labelClass}>Preferred Majors</label>
                                                        <input type="text" value={editingJd.PreferredMajors || ''} onChange={(e) => setEditingJd({ ...editingJd, PreferredMajors: e.target.value })} className={inputClass} />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleUpdateJd(editingJd)} disabled={saving} className={btnPrimary}><Save size={16} /> Save</button>
                                                        <button onClick={() => setEditingJd(null)} className={btnSecondary}><X size={16} /> Cancel</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3 flex-1">
                                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.RoleName}</span>
                                                            {item.TypicalIndustries && (
                                                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                    {item.TypicalIndustries.split(';').slice(0, 3).join(', ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => setExpandedId(expandedId === `jd-${item.JobDescriptionID}` ? null : `jd-${item.JobDescriptionID}`)}
                                                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}
                                                                title="View details"
                                                            >
                                                                {expandedId === `jd-${item.JobDescriptionID}` ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                            </button>
                                                            <button onClick={() => setEditingJd({ ...item })} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                                                <Edit size={16} />
                                                            </button>
                                                            {jdStatusTab === 'active' ? (
                                                                <button onClick={() => handleDeleteJd(item)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Deactivate">
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            ) : (
                                                                <button onClick={() => handleRestoreJd(item)} className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Restore">
                                                                    <RotateCcw size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {expandedId === `jd-${item.JobDescriptionID}` && (
                                                        <div className={`mt-3 p-3 rounded-lg text-sm space-y-2 ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                                                            <p>{item.RoleSummary}</p>
                                                            {item.Responsibilities && <p><strong>Responsibilities:</strong> {item.Responsibilities}</p>}
                                                            {item.RequiredSkills && <p><strong>Required Skills:</strong> {item.RequiredSkills}</p>}
                                                            {item.PreferredMajors && <p><strong>Preferred Majors:</strong> {item.PreferredMajors}</p>}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ════════ RESUME CHECKLIST TAB ════════ */}
                {activeTab === 'checklist' && (
                    <div className="space-y-6">
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            Students enter an organization code on the Resume Checklist tab to check a resume against that organization's standards.
                            Organization codes live in <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>MembershipReferralCodes</code> and
                            their checklist items in <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>OrgChecklistItems</code>.
                            The prompt below applies to every organization. Monthly run limits per tier are set in Membership Management.
                        </p>
                        <PromptTab isDarkMode={isDarkMode} featureKey="org_checklist" />
                    </div>
                )}

            </div>
        </div>
    );
}
